import React, { useState } from "react";
import styles from "./LoginView.module.css";

const ASLAN_MARK_URL = "assets/brand-mark-red.png";
const ASLAN_WORDMARK_URL = "assets/brand-wordmark.png";

// Microsoft logo SVG (official brand mark)
function MsIcon() {
  return (
    <svg
      className={styles.msIcon}
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

interface LoginViewProps {
  onLogin: () => Promise<void>;
}

export default function LoginView({ onLogin }: LoginViewProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await onLogin();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed";
      // Suppress user-cancelled popup — not an error
      if (msg.startsWith("ACCOUNT_MISMATCH:")) {
        // Show the wrong-account guidance verbatim (it names both addresses).
        setError(msg.slice("ACCOUNT_MISMATCH:".length));
      } else if (!msg.includes("user_cancelled") && !msg.includes("popup_window_error")) {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.logoWrap}>
        <img
          src={ASLAN_MARK_URL}
          alt="ASLAN"
          className={styles.logoImg}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
            const next = e.currentTarget.nextSibling as HTMLElement;
            if (next) next.style.display = "block";
          }}
        />
        <span className={styles.logoFallback} style={{ display: "none" }}>
          ASLAN
        </span>
        <img src={ASLAN_WORDMARK_URL} alt="ASLAN" className={styles.wordmark} />
      </div>

      <p className={styles.tagline}>
        Your AI-powered email coaching assistant
      </p>

      <button
        className={styles.msButton}
        onClick={handleLogin}
        disabled={loading}
      >
        <MsIcon />
        {loading ? "Signing in…" : "Sign in with Microsoft"}
      </button>

      {error && <p className={styles.error}>{error}</p>}

      <p className={styles.caption}>Powered by Microsoft Azure &amp; ASLAN+</p>
    </div>
  );
}
